import { Alert, Button, DatePicker, Form, List, Table } from 'antd';

import apiCompaniesCall from 'apps/futureflex/apiCalls/company/list';
import apiCall from 'apps/futureflex/apiCalls/employeeProgramOrder';

import MetaDecorator from 'apps/home/components/MetaDecorator';
import PageTitle from 'apps/home/components/PageTitle';
import { orderProgramTypeFlex } from 'apps/investment/constants/fundOrder';
import SelectListData from 'common/form/SelectListData';
import { formatTableColumns } from 'common/ui/DataTable/config';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchApiList, usePostApi } from 'hooks/api';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { convertDateFormData, ensureDateFields } from 'utils/formData';
import { rebundleColumns } from './config';

const title = 'Rebundle All';

function parseSearchParams(searchParams) {
  const parsed = QueryString.parse(searchParams.toString());
  return {
    company_id: parsed.company_id ? Number(parsed.company_id) : undefined,
    start_date: parsed.start_date ? moment(parsed.start_date, 'YYYY-MM-DD') : undefined,
  };
}

const RebundleOrdersV2Form = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [form] = Form.useForm();
  const [previewParams, setPreviewParams] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [cleanupDryRunResult, setCleanupDryRunResult] = useState(null);

  // cancel-all / rebundle-all / cleanup-all all commit their transaction
  // before the handler returns, so refetching the list is enough — no reload.
  const refreshOrders = () => {
    queryClient.invalidateQueries({ queryKey: [apiCall.list.queryKey] });
  };

  const { post: cancelOrders, isLoading: cancelling } = usePostApi(
    apiCall.cancelBatch,
    {
      resourceName: 'data',
      onSuccess: refreshOrders,
    }
  );

  const { post: rebundleOrders, isLoading: rebundling } = usePostApi(
    apiCall.rebundleAll,
    {
      resourceName: 'data',
      onSuccess: refreshOrders,
    }
  );

  const { post: dryRunRebundleOrders, isLoading: dryRunning } = usePostApi(
    apiCall.rebundleAll,
    {
      resourceName: 'data',
      onSuccess: response => {
        // Dry run writes nothing, so just show the result - no refetch.
        setDryRunResult(response?.data ?? response);
      },
    }
  );

  const { post: cleanupOrders, isLoading: cleaning } = usePostApi(
    apiCall.cleanupAll,
    {
      resourceName: 'data',
      onSuccess: refreshOrders,
    }
  );

  const { post: dryRunCleanupOrders, isLoading: cleanupDryRunning } = usePostApi(
    apiCall.cleanupAll,
    {
      resourceName: 'data',
      onSuccess: response => {
        // Dry run writes nothing, so just show the result - no page reload.
        setCleanupDryRunResult(response?.data ?? response);
      },
    }
  );

  const { company_id, start_date } = Form.useWatch([], form) || {};

  // Initialize form with URL params on mount and when searchParams change
  useEffect(() => {
    const params = parseSearchParams(searchParams);

    // Only set form values if we have params from URL
    if (params.company_id || params.start_date) {
      form.setFieldsValue(params);
    }

    // Auto-load preview if URL has both params (only on initial mount)
    if (params.company_id && params.start_date && !previewParams) {
      const formattedDate = moment(params.start_date).format('YYYY-MM-DD');
      setPreviewParams({
        company_id: params.company_id,
        order_date__gte: formattedDate,
        program_type: orderProgramTypeFlex,
      });
    }
  }, [searchParams.toString()]);

  const handleShowPreview = values => {
    const { company_id, start_date } = convertDateFormData(values, [
      'start_date',
    ]);
    if (company_id && start_date) {
      // Format the date - start_date is a moment object from DatePicker
      const formattedDate = moment.isMoment(start_date)
        ? start_date.format('YYYY-MM-DD')
        : moment(start_date).format('YYYY-MM-DD');

      // Update query string params
      setSearchParams({
        company_id: company_id.toString(),
        start_date: formattedDate,
      }, { replace: true });

      setPreviewParams({
        company_id,
        order_date__gte: formattedDate,
        program_type: orderProgramTypeFlex,
      });
      setDryRunResult(null);
    }
  };

  const handleDryRunRebundle = () => {
    const values = form.getFieldsValue();
    const { company_id, start_date } = convertDateFormData(values, ['start_date']);

    if (!company_id || !start_date) {
      alert('Please select both company and start date');
      return;
    }

    setDryRunResult(null);
    dryRunRebundleOrders({
      payload: {
        company_id,
        start_date,
        dry_run: true,
      },
    });
  };

  const handleCancelOrders = () => {
    const values = form.getFieldsValue();
    const { company_id, start_date } = convertDateFormData(values, ['start_date']);

    if (!company_id || !start_date) {
      alert('Please select both company and start date');
      return;
    }

    if (
      confirm(
        `Are you sure you want to cancel ALL flex orders for the selected company and start date: ${start_date}?`
      )
    ) {
      cancelOrders({
        payload: {
          company_id,
          start_date,
        },
      });
    }
  };

  const handleRebundleOrders = () => {
    const values = form.getFieldsValue();
    const { company_id, start_date } = convertDateFormData(values, ['start_date']);

    if (!company_id || !start_date) {
      alert('Please select both company and start date');
      return;
    }

    if (
      confirm(
        `Are you sure you want to rebundle ALL flex orders for the selected company and start date: ${start_date}?`
      )
    ) {
      rebundleOrders({
        payload: {
          company_id,
          start_date,
        },
      });
    }
  };

  const handleDryRunCleanup = () => {
    const values = form.getFieldsValue();
    const { company_id, start_date } = convertDateFormData(values, ['start_date']);

    if (!company_id || !start_date) {
      alert('Please select both company and start date');
      return;
    }

    setCleanupDryRunResult(null);
    dryRunCleanupOrders({
      payload: {
        company_id,
        start_date,
        dry_run: true,
      },
    });
  };

  const handleCleanupOrders = () => {
    const values = form.getFieldsValue();
    const { company_id, start_date } = convertDateFormData(values, ['start_date']);

    if (!company_id || !start_date) {
      alert('Please select both company and start date');
      return;
    }

    if (
      confirm(
        `Are you sure you want to PERMANENTLY DELETE all cancelled flex orders (and any unsent related fund order) for the selected company and start date: ${start_date}? This cannot be undone.`
      )
    ) {
      cleanupOrders({
        payload: {
          company_id,
          start_date,
        },
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <MetaDecorator title={title} />
      <PageTitle title={title} backUrl={`/futureflex/program-orders`} />
      <Alert
        type="info"
        description={
          <List
            size="small"
            dataSource={[
              'This page allows you to manage flex orders by company and start date.',
              'Select a company and start date to preview all matching flex orders (from start date onwards).',
              'Click "Show" to load the preview.',
              '"Dry Run" - Preview what "Rebundle All Orders" would do (how many orders would be rebundled or backfilled) without writing anything.',
              '"Cancel All Orders" - Cancel ALL orders shown in the preview.',
              '"Rebundle All Orders" - Rebundle ALL cancelled orders shown in the preview, and backfill any missing month\'s orders, up to today.',
              '"Cleanup Cancelled Orders" - PERMANENTLY DELETE cancelled program orders and any related fund order that hasn\'t been sent to VCAM yet (fund order id still empty). Use this to clear out junk left by a failed/duplicate rebundle before retrying it.',
              'Warning: "Cancel All Orders", "Rebundle All Orders" and "Cleanup Cancelled Orders" cannot be undone. Use "Dry Run" first to verify.',
            ]}
            renderItem={item => <List.Item>{item}</List.Item>}
          />
        }
      />

      <Form
        form={form}
        name={'rebundle_orders_v2_form'}
        scrollToFirstError
        layout="vertical"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <Form.Item
            name="company_id"
            label="Company"
            rules={[{ required: true, message: 'Please select a company' }]}
            className="mb-0"
          >
            <SelectListData
              apiCall={apiCompaniesCall}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              placeholder="Search Email / Full company name"
              callback={({ id, email, name }) => ({
                value: id,
                label: name + ' / ' + email,
              })}
              onChange={(value, option) => {
                setSelectedCompanyName(option?.label || '');
              }}
            />
          </Form.Item>

          <Form.Item
            name="start_date"
            label="Start Date (Order Date)"
            rules={[{ required: true, message: 'Please select a start date' }]}
            className="mb-0"
          >
            <DatePicker
              className="w-full"
              format="YYYY-MM-DD"
              placeholder="Select start date"
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <Button
              type="primary"
              onClick={() => handleShowPreview(form.getFieldsValue())}
              disabled={!company_id || !start_date}
            >
              Show
            </Button>
          </Form.Item>
        </div>

        {previewParams ? (
          <>
            <PreviewTable apiCall={apiCall.list} params={previewParams} />

            {dryRunResult ? (
              <Alert
                className="mt-4"
                type="success"
                showIcon
                message="Dry run result (nothing was written)"
                description={
                  <>
                    Would rebundle <b>{dryRunResult.rebundled_count ?? 0}</b>{' '}
                    cancelled order(s) and create{' '}
                    <b>{dryRunResult.created_count ?? 0}</b> new order(s) for
                    missing month(s).
                  </>
                }
                closable
                onClose={() => setDryRunResult(null)}
              />
            ) : null}

            {cleanupDryRunResult ? (
              <Alert
                className="mt-4"
                type="success"
                showIcon
                message="Cleanup dry run result (nothing was written)"
                description={
                  <>
                    Would delete{' '}
                    <b>{cleanupDryRunResult.deleted_program_order_count ?? 0}</b>{' '}
                    cancelled program order(s) and{' '}
                    <b>{cleanupDryRunResult.deleted_fund_order_count ?? 0}</b>{' '}
                    unsent fund order(s).
                  </>
                }
                closable
                onClose={() => setCleanupDryRunResult(null)}
              />
            ) : null}

            <div className="mt-4 flex gap-2">
              <Button
                onClick={handleDryRunRebundle}
                loading={dryRunning}
                disabled={cancelling || rebundling || cleaning || cleanupDryRunning}
              >
                Dry Run
              </Button>
              <Button
                type="primary"
                danger
                onClick={handleCancelOrders}
                loading={cancelling}
                disabled={rebundling || dryRunning || cleaning || cleanupDryRunning}
              >
                Cancel All Orders
              </Button>
              <Button
                type="primary"
                onClick={handleRebundleOrders}
                loading={rebundling}
                disabled={cancelling || dryRunning || cleaning || cleanupDryRunning}
                style={{
                  backgroundColor: 'green',
                  borderColor: 'green',
                }}
              >
                Rebundle All Orders
              </Button>
              <Button
                onClick={handleDryRunCleanup}
                loading={cleanupDryRunning}
                disabled={cancelling || rebundling || dryRunning || cleaning}
              >
                Cleanup Dry Run
              </Button>
              <Button
                type="primary"
                danger
                onClick={handleCleanupOrders}
                loading={cleaning}
                disabled={cancelling || rebundling || dryRunning || cleanupDryRunning}
              >
                Cleanup Cancelled Orders
              </Button>
            </div>
          </>
        ) : null}
      </Form>
    </div>
  );
};

export default RebundleOrdersV2Form;

export const PreviewTable = ({ apiCall, params }) => {
  const {
    data = [],
    load,
    isLoading,
    refreshing,
    error,
    paging,
  } = useFetchApiList(apiCall, { resourceName: 'data' });

  useEffect(() => {
    // Load all results without pagination by setting a large page_size
    load({ ...params, page_size: 9999 });
  }, [JSON.stringify(params)]);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!!error) {
    return <ErrorMessage errors={error} />;
  }
  const { count } = paging || {};

  return (
    <>
      <div className="mb-2 text-sm font-semibold text-gray-700">
        {count > 0 ? (
          <>
            Preview: Found {count} flex order(s) from the selected start date onwards.
          </>
        ) : (
          'No flex orders found for the selected company and start date.'
        )}
      </div>
      <Table
        rowKey={e => e?.id}
        size="small"
        dataSource={data}
        loading={refreshing}
        columns={formatTableColumns(rebundleColumns)}
        pagination={false}
      />
    </>
  );
};
